import { Wallet, ArrowUpRight, ArrowDownLeft, RefreshCw, Plus, History, TrendingUp, PieChart } from 'lucide-react';
import { cn } from '../../../utils/helpers';
import { useTranslation } from '../../../hooks/useTranslation';
import { useLocale } from '../../../hooks/useLocale';

export default function WalletPage() {
  const { t } = useTranslation();
  const { formatCurrency, formatNumber, firstDayOfWeek } = useLocale();
  return (
    <div className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('wallet.title')}</h1>
          <p className="text-neutral-500 dark:text-neutral-400">{t('wallet.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
            <RefreshCw className="w-4 h-4" />
            {t('wallet.sync')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white rounded-xl text-sm font-medium hover:bg-neutral-800 transition-colors shadow-lg shadow-neutral-900/20">
            <Plus className="w-4 h-4" />
            {t('wallet.topUp')}
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Balance Card */}
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-neutral-900 rounded-[2.5rem] p-8 md:p-12 text-white relative overflow-hidden shadow-2xl shadow-neutral-900/30">
            <div className="relative z-10">
              <div className="flex items-center gap-2 text-neutral-400 dark:text-neutral-500 mb-4">
                <Wallet className="w-5 h-5" />
                <span className="text-sm font-bold uppercase tracking-widest">{t('wallet.totalBalance')}</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold tracking-tight mb-8">{formatCurrency(12450)}</h2>
              <div className="flex flex-wrap gap-4">
                <button className="flex items-center gap-2 bg-white text-neutral-900 px-6 py-3 rounded-2xl font-bold hover:bg-neutral-100 transition-all">
                  <ArrowUpRight className="w-5 h-5" />
                  {t('wallet.send')}
                </button>
                <button className="flex items-center gap-2 bg-white/10 text-white px-6 py-3 rounded-2xl font-bold hover:bg-white/20 transition-all backdrop-blur-md">
                  <ArrowDownLeft className="w-5 h-5" />
                  {t('wallet.request')}
                </button>
                <button className="flex items-center gap-2 bg-white/10 text-white px-6 py-3 rounded-2xl font-bold hover:bg-white/20 transition-all backdrop-blur-md">
                  <History className="w-5 h-5" />
                  {t('wallet.history')}
                </button>
              </div>
            </div>
            {/* Abstract background elements */}
            <div className="absolute -right-20 -bottom-20 w-80 h-80 bg-white/5 rounded-full blur-3xl"></div>
            <div className="absolute left-1/2 top-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-x-1/2"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-neutral-900 p-8 rounded-[2rem] border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-lg">{t('wallet.spendingAnalysis')}</h3>
                <PieChart className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
              </div>
              <div className="space-y-4">
                {[
                  { label: t('wallet.shopping'), amount: formatCurrency(4200), color: 'bg-neutral-900', width: 'w-[65%]' },
                  { label: t('wallet.bills'), amount: formatCurrency(1800), color: 'bg-neutral-400', width: 'w-[35%]' },
                  { label: t('wallet.entertainment'), amount: formatCurrency(950), color: 'bg-neutral-200 dark:bg-neutral-800', width: 'w-[20%]' },
                ].map((item) => (
                  <div key={item.label} className="space-y-2">
                    <div className="flex justify-between text-sm font-bold">
                      <span>{item.label}</span>
                      <span>{item.amount}</span>
                    </div>
                    <div className="w-full bg-neutral-50 dark:bg-neutral-950 h-2 rounded-full overflow-hidden">
                      <div className={cn("h-full rounded-full", item.color, item.width)}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-neutral-900 p-8 rounded-[2rem] border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-lg">{t('wallet.growth')}</h3>
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
              <div className="h-32 flex items-end gap-2">
                {[40, 70, 45, 90, 65, 80, 100].map((h, i) => (
                  <div 
                    key={i} 
                    className="flex-1 bg-neutral-100 dark:bg-neutral-800 rounded-t-lg hover:bg-neutral-900 transition-all cursor-pointer group relative"
                    style={{ height: `${h}%` }}
                  >
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-neutral-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      {formatCurrency(h * 10)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-4 text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">
                <span>{firstDayOfWeek === 'monday' ? t('common.monday') : t('common.sunday')}</span>
                <span>{firstDayOfWeek === 'monday' ? t('common.sunday') : t('common.saturday')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Assets Sidebar */}
        <div className="space-y-6">
          <h2 className="text-xl font-bold">{t('wallet.assets')}</h2>
          <div className="space-y-4">
            {[
              { name: t('wallet.usDollar'), symbol: 'USD', balance: formatNumber(8450, { minimumFractionDigits: 2 }), icon: '$', color: 'bg-emerald-500' },
              { name: t('wallet.bitcoin'), symbol: 'BTC', balance: formatNumber(0.1245, { maximumFractionDigits: 4 }), icon: '₿', color: 'bg-amber-500' },
              { name: t('wallet.ethereum'), symbol: 'ETH', balance: formatNumber(2.5), icon: 'Ξ', color: 'bg-indigo-500' },
              { name: t('wallet.messegoToken'), symbol: 'MSG', balance: formatNumber(12000), icon: 'M', color: 'bg-neutral-900' },
            ].map((asset) => (
              <div key={asset.symbol} className="bg-white dark:bg-neutral-900 p-4 rounded-3xl border border-neutral-200 dark:border-neutral-800 flex items-center justify-between hover:border-neutral-900 transition-all cursor-pointer group">
                <div className="flex items-center gap-4">
                  <div className={cn("w-12 h-12 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg", asset.color)}>
                    {asset.icon}
                  </div>
                  <div>
                    <p className="font-bold text-sm">{asset.name}</p>
                    <p className="text-[10px] text-neutral-500 dark:text-neutral-400 font-bold">{asset.symbol}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm">{asset.balance}</p>
                  <p className="text-[10px] text-emerald-500 font-bold">+2.4%</p>
                </div>
              </div>
            ))}
          </div>
          <button className="w-full py-4 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-3xl font-bold text-sm text-neutral-500 dark:text-neutral-400 hover:border-neutral-900 hover:text-neutral-900 dark:hover:text-neutral-50 transition-all">
            {t('wallet.manageAssets')}
          </button>
        </div>
      </div>
    </div>
  );
}
